/**
 * CascadeStatusModal — confirmation modal for cascade-Inactivo / cascade-Closed
 * Agreement status transitions (#262, 2026-06-08 design contract).
 *
 * Renders the preview of descendants that will be touched by the cascade,
 * grouped by category with summary counts + collapsible detail lists.
 * Stronger warning copy for Closed transitions (permanent terminal state).
 *
 * Two consumption modes:
 *   - Interactive (default): used by AgreementDetailPage on Save with a
 *     cascade-triggering status change. Cancel reverts the form; Confirm
 *     proceeds with the mutation (which the parent supplies).
 *   - Read-only: used by ChangeRequestDetailPage to show the approver
 *     the same preview from the stored cascade_plan in the CR payload.
 *
 * Charter §5: human-in-the-loop. The modal IS the human checkpoint for
 * cascade-triggering status changes. CONFIRM-gate matches the framework's
 * footprint-preview + CONFIRM-gate friction model.
 */
import {
  Alert, Badge, Button, Collapse, Group, Modal, Stack, Text, Title,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangle, IconChevronDown, IconChevronRight, IconShieldCheck,
} from '@tabler/icons-react';
import { useState } from 'react';

import type { CascadeFootprint, LifecycleStatus } from '../api/agreements';

export interface CascadeStatusModalProps {
  opened: boolean;
  onClose: () => void;
  /** The footprint to render. Null while loading. */
  footprint: CascadeFootprint | null | undefined;
  /** True while the apply mutation is in flight. */
  loading?: boolean;
  /** Called when the user clicks the primary action. */
  onConfirm: () => void;
  /** When true, the modal renders without a primary action (read-only).
   *  Used by the CR detail page for approver preview. */
  readOnly?: boolean;
  /** Optional label to display in title (e.g. Baluarte code or mnemonic). */
  agreementLabel?: string;
  /** Override the primary-button label. Defaults to "Confirm & file" or
   *  "Confirm & apply" based on `pendingApproval`. */
  primaryLabel?: string;
  /** When true (the maker-checker route), the button reads "Confirm & file";
   *  when false (direct-apply), it reads "Confirm & apply". */
  pendingApproval?: boolean;
  /** 'reactivate' flips the copy/colour for the faithful-reactivate flow
   *  (Phase 3 — restoring children + reopening) vs the default inactivate/close
   *  cascade. The footprint shape is identical. */
  mode?: 'cascade' | 'reactivate';
}

function Section({
  title,
  count,
  rows,
  emptyMessage,
}: {
  title: string;
  count: number;
  rows: Array<{ id: string; label: string; via_policy_label?: string | null }>;
  emptyMessage?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Stack gap={4}>
      <UnstyledButton
        onClick={() => setOpen((v) => !v)}
        disabled={count === 0}
        style={{ cursor: count > 0 ? 'pointer' : 'default' }}
      >
        <Group gap="xs" wrap="nowrap">
          {count > 0 ? (
            open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />
          ) : (
            <span style={{ width: 14 }} />
          )}
          <Text size="sm" fw={500}>{title}</Text>
          <Badge variant="light" size="sm" color={count > 0 ? 'orange' : 'gray'}>
            {count}
          </Badge>
        </Group>
      </UnstyledButton>
      {count === 0 && emptyMessage && (
        <Text size="xs" c="dimmed" pl="lg">{emptyMessage}</Text>
      )}
      <Collapse in={open && count > 0}>
        <Stack gap={2} pl="lg">
          {rows.map((r) => (
            <Text key={r.id} size="sm">
              • {r.label}
              {r.via_policy_label && (
                <Text span size="xs" c="dimmed"> (via {r.via_policy_label})</Text>
              )}
            </Text>
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
}

export function CascadeStatusModal({
  opened,
  onClose,
  footprint,
  loading = false,
  onConfirm,
  readOnly = false,
  agreementLabel,
  primaryLabel,
  pendingApproval = false,
  mode = 'cascade',
}: CascadeStatusModalProps) {
  const toStatus: LifecycleStatus | undefined = footprint?.to_status;
  const isClosed = toStatus === 'Closed';
  const isReactivate = mode === 'reactivate';

  const headerColor = isReactivate ? 'green' : isClosed ? 'red' : 'orange';
  const titleText = isReactivate
    ? `Reactivate this Agreement${agreementLabel ? ` (${agreementLabel})` : ''}?`
    : toStatus
    ? `Mark this Agreement ${toStatus}${agreementLabel ? ` (${agreementLabel})` : ''}?`
    : 'Cascade preview';

  const total = footprint
    ? footprint.summary.direct_accounts
      + footprint.summary.policies
      + footprint.summary.indirect_accounts
    : 0;

  const alertCopy = isReactivate
    ? (
      <>
        Reactivating this Agreement reopens it to <strong>{toStatus}</strong> and
        restores <strong>{total}</strong> child record{total === 1 ? '' : 's'} to
        Active — exactly the records its inactivation moved. Children that were
        already inactive independently are left as they are.
      </>
    )
    : isClosed
    ? (
      <>
        <strong>This action is permanent.</strong> Closing this Agreement
        also closes <strong>{total}</strong> descendant
        record{total === 1 ? '' : 's'}. Closed entities cannot be re-activated.
        Policies will be detached from this Agreement.
      </>
    )
    : (
      <>
        Marking this Agreement Inactive also marks <strong>{total}</strong>{' '}
        descendant record{total === 1 ? '' : 's'} Inactive. Links are kept —
        policies and accounts stay attached (Phase 2). This is reversible:
        reactivating the Agreement restores exactly these records (faithful
        reactivate).
      </>
    );

  const effectivePrimaryLabel = primaryLabel ?? (pendingApproval ? 'Confirm & file' : 'Confirm & apply');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Title order={4}>{titleText}</Title>}
      centered
      size="lg"
      // Per Mantine theme #260: closeOnClickOutside defaults to false app-wide.
      // Explicitly true while loading would let users escape a mid-flight
      // mutation; leave at theme default for consistency.
    >
      <Stack gap="md">
        <Alert
          icon={<IconAlertTriangle size={20} />}
          color={headerColor}
          variant="light"
        >
          {footprint
            ? alertCopy
            : 'Loading cascade preview…'}
        </Alert>

        {footprint && (
          <Stack gap="sm">
            <Section
              title={isReactivate ? 'Direct Accounts (restored)' : 'Direct Accounts'}
              count={footprint.summary.direct_accounts}
              rows={footprint.direct_accounts}
              emptyMessage="No Direct Accounts will be affected."
            />
            <Section
              title={isReactivate ? 'Insurance Policies (restored)' : 'Insurance Policies'}
              count={footprint.summary.policies}
              rows={footprint.policies}
              emptyMessage="No Policies attached to this Agreement."
            />
            <Section
              title={isReactivate ? 'Indirect Accounts (restored)' : 'Indirect Accounts (under affected Policies)'}
              count={footprint.summary.indirect_accounts}
              rows={footprint.indirect_accounts}
              emptyMessage="No Indirect Accounts under affected Policies."
            />
          </Stack>
        )}

        {footprint && total === 0 && (
          <Text size="sm" c="dimmed">
            <IconShieldCheck size={14} style={{ verticalAlign: 'middle' }} />
            {' '}No descendants will be affected. Only this Agreement's status
            changes.
          </Text>
        )}

        {!readOnly && (
          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              color={headerColor}
              onClick={onConfirm}
              disabled={!footprint || loading}
              loading={loading}
            >
              {isClosed
                ? `${effectivePrimaryLabel} (permanent)`
                : effectivePrimaryLabel}
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}
